import React, { useState, useEffect } from 'react';
import { emailInboxAPI, eaAPI } from '../services/api';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, Inbox, Loader2, MailSearch, Building2, MapPin, Calendar, FileText,
  CheckCircle2, X, Wand2, RefreshCw, User, Hash, Paperclip, Link as LinkIcon,
  Bot, Zap, MailCheck, Send, Reply, CalendarRange, Mailbox, Calculator, AlertTriangle, FilePlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const TIMEZONES = [
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'UTC', label: 'UTC' },
];

const formatHour = (h) => {
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
};

const sampleEmail = {
  from_address: 'po@allinsure.com',
  subject: 'New Purchase Order PO-88123 - Make Safe & Bathroom Repair',
  body: `Hi team,

Please find PO-88123 for insurance claim CLM-9921.

Site address: 12 Marine Parade, Southport QLD 4215
Insured: Mrs Karen Wells (0400 111 222)

Scope: Emergency make safe, remove damaged vanity, waterproof and re-tile bathroom, replace vanity and repaint.

We require you on site on Monday 11 August 2025. Trades needed: Plumber, Tiler, Painter.

Line items:
- Make safe: 1 @ $350
- Waterproofing: 1 @ $600
- Re-tile bathroom: 12 @ $85

Regards,
Sarah — AllInsure Builders`,
};

export const POInboxPage = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [account, setAccount] = useState(null);
  const [ea, setEa] = useState(null);
  const [reports, setReports] = useState(null);
  const [runningEa, setRunningEa] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);

  // Test dialog
  const [testOpen, setTestOpen] = useState(false);
  const [testData, setTestData] = useState({ from_address: '', subject: '', body: '' });
  const [testLoading, setTestLoading] = useState(false);

  // AI reply dialog
  const [reply, setReply] = useState({ open: false, cand: null, intent: 'accept', to: '', subject: '', body: '', loading: false, sending: false });

  const openReply = async (cand, intent) => {
    setReply({ open: true, cand, intent, to: cand.from_address || '', subject: '', body: '', loading: true, sending: false });
    try {
      const res = await emailInboxAPI.draftReply(cand.id, { intent });
      setReply((r) => ({ ...r, to: res.data.to, subject: res.data.subject, body: res.data.body, loading: false }));
    } catch (e) {
      toast.error('Could not draft reply');
      setReply((r) => ({ ...r, loading: false }));
    }
  };

  const regenReply = async () => {
    if (!reply.cand) return;
    setReply((r) => ({ ...r, loading: true }));
    try {
      const res = await emailInboxAPI.draftReply(reply.cand.id, { intent: reply.intent });
      setReply((r) => ({ ...r, body: res.data.body, subject: res.data.subject, loading: false }));
    } catch (e) {
      toast.error('Could not redraft');
      setReply((r) => ({ ...r, loading: false }));
    }
  };

  const sendReply = async () => {
    setReply((r) => ({ ...r, sending: true }));
    try {
      const res = await emailInboxAPI.sendReply(reply.cand.id, { to: reply.to, subject: reply.subject, body: reply.body, intent: reply.intent });
      if (res.data.email_delivered === false) {
        toast.warning(res.data.warning || 'Email delivery failed');
      } else {
        toast.success(`${reply.intent === 'decline' ? 'Decline' : 'Accept'} reply sent to ${reply.to}`);
      }
      setReply({ open: false, cand: null, intent: 'accept', to: '', subject: '', body: '', loading: false, sending: false });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
      setReply((r) => ({ ...r, sending: false }));
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [inboxRes, accRes, eaRes, repRes] = await Promise.all([
        emailInboxAPI.getInbox(),
        emailInboxAPI.getAccount().catch(() => ({ data: null })),
        eaAPI.getSettings().catch(() => ({ data: null })),
        eaAPI.getReportSettings().catch(() => ({ data: null })),
      ]);
      setCandidates(inboxRes.data || []);
      setAccount(accRes.data || null);
      setEa(eaRes.data || null);
      setReports(repRes.data || null);
    } catch (e) {
      toast.error('Failed to load PO inbox');
    } finally {
      setLoading(false);
    }
  };

  const updateEa = async (patch) => {
    const prev = ea;
    setEa({ ...ea, ...patch });
    try {
      const res = await eaAPI.updateSettings(patch);
      setEa(res.data);
    } catch (e) {
      setEa(prev);
      toast.error(e.response?.data?.detail || 'Could not update automation settings');
    }
  };

  const updateReports = async (patch) => {
    try {
      const res = await eaAPI.updateReportSettings(patch);
      setReports(res.data);
      toast.success('Report settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save report settings');
    }
  };

  const handleTestBrief = async () => {
    setSendingTest(true);
    try {
      const res = await eaAPI.sendTestDigest();
      if (res.data.sent) {
        toast.success(`Test brief sent to ${res.data.email}`);
      } else {
        toast.warning(res.data.warning || 'Could not send test brief');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test brief');
    } finally {
      setSendingTest(false);
    }
  };

  const handleWeeklyTest = async () => {
    setSendingWeekly(true);
    try {
      const res = await eaAPI.sendWeeklyTest();
      if (res.data.sent) {
        toast.success(`Week-ahead email sent to ${res.data.email}`);
      } else {
        toast.warning(res.data.warning || 'Could not send week-ahead email');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send week-ahead email');
    } finally {
      setSendingWeekly(false);
    }
  };

  const handleRunEa = async () => {    setRunningEa(true);
    try {
      const res = await eaAPI.runNow();
      if (res.data.status === 'already_running') {
        toast.info('The assistant is already working…');
      } else {
        toast.info('Your AI assistant is scanning the inbox… this can take up to a minute.');
      }
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const st = await emailInboxAPI.scanStatus();
          if (!st.data.scanning || tries > 40) {
            clearInterval(poll);
            setRunningEa(false);
            if (st.data.last_scan_error) {
              toast.error(`Assistant failed: ${st.data.last_scan_error}`);
            } else {
              const imported = st.data.last_ea_auto_imported;
              const found = st.data.last_scan_result?.new_po_candidates ?? 0;
              toast.success(
                imported != null
                  ? `Assistant done — ${found} PO(s) found, ${imported} auto-imported`
                  : `Assistant done — ${found} PO(s) found`
              );
            }
            fetchAll();
          }
        } catch (e) {
          clearInterval(poll);
          setRunningEa(false);
        }
      }, 3000);
    } catch (e) {
      setRunningEa(false);
      toast.error(e.response?.data?.detail || 'Assistant run failed');
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await emailInboxAPI.scan();
      if (res.data.status === 'already_running') {
        toast.info('A scan is already running…');
      } else {
        toast.info('Scanning your inbox with AI… this can take up to a minute.');
      }
      // Poll for completion
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const st = await emailInboxAPI.scanStatus();
          if (!st.data.scanning || tries > 40) {
            clearInterval(poll);
            setScanning(false);
            if (st.data.last_scan_error) {
              toast.error(`Scan failed: ${st.data.last_scan_error}`);
            } else if (st.data.last_scan_result) {
              const r = st.data.last_scan_result;
              toast.success(`Scanned ${r.scanned} emails · ${r.new_po_candidates} new PO(s) found`);
            } else {
              toast.success('Scan complete');
            }
            fetchAll();
          }
        } catch (e) {
          clearInterval(poll);
          setScanning(false);
        }
      }, 3000);
    } catch (e) {
      setScanning(false);
      toast.error(e.response?.data?.detail || 'Scan failed');
    }
  };

  const handleTestParse = async () => {
    if (!testData.body.trim()) {
      toast.error('Paste the email body first');
      return;
    }
    setTestLoading(true);
    try {
      await emailInboxAPI.createTest(testData);
      toast.success('AI parsed the email and added it to your PO inbox');
      setTestOpen(false);
      setTestData({ from_address: '', subject: '', body: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI parsing failed');
    } finally {
      setTestLoading(false);
    }
  };

  const handleImport = async (id) => {
    try {
      await emailInboxAPI.importCandidate(id);
      toast.success('Job created from PO');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
  };

  const handleDismiss = async (id) => {
    try {
      await emailInboxAPI.dismissCandidate(id);
      fetchAll();
    } catch (e) {
      toast.error('Failed to dismiss');
    }
  };

  const handleCreateQuote = async (id) => {
    try {
      const res = await api.post(`/po-inbox/${id}/create-quote`);
      toast.success(`Draft quote ${res.data.quote_number} created — opening it now`, {
        description: `${res.data.item_count} line items · $${Number(res.data.total).toLocaleString()}`,
        duration: 4000,
      });
      navigate(`/quotes?openQuote=${res.data.quote_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create quote');
    }
  };

  const pending = candidates.filter((c) => c.status === 'pending');
  const processed = candidates.filter((c) => c.status !== 'pending');

  const statusBadge = (s) => {
    if (s === 'imported') return <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Imported</Badge>;
    if (s === 'dismissed') return <Badge className="bg-slate-500/15 text-slate-300 border-slate-500/30">Dismissed</Badge>;
    return <Badge className="bg-primary/15 text-primary border-primary/30">Pending</Badge>;
  };

  const renderCard = (c) => {
    const p = c.parsed || {};
    const conf = Math.round((p.confidence || 0) * 100);
    return (
      <Card key={c.id} className="surface card-hover" data-testid={`po-candidate-${c.id}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center flex-shrink-0 shadow-glow">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold truncate">{p.description || c.subject || 'Purchase Order'}</h3>
                  {statusBadge(c.status)}
                  {c.auto_imported && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 flex items-center gap-1">
                      <Bot className="h-2.5 w-2.5" /> AI auto
                    </span>
                  )}
                  {c.has_pdf && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 flex items-center gap-1">
                      <Paperclip className="h-2.5 w-2.5" /> PDF read
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  From {c.from_address || 'unknown'} · {c.subject}
                </p>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="text-xs text-muted-foreground">AI confidence</span>
              <div className={`text-sm font-bold ${conf >= 80 ? 'text-emerald-400' : conf >= 50 ? 'text-amber-400' : 'text-rose-400'}`}>{conf}%</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm">
            <Detail icon={Hash} label="PO Reference" value={p.po_number} mono />
            <Detail icon={Building2} label="Supplier" value={p.supplier_name} />
            <Detail icon={MapPin} label="Site Address" value={p.site_address} />
            <Detail icon={User} label="Site Contact" value={p.client_name} />
            <Detail
              icon={Calendar}
              label="On-site Date"
              value={p.on_site_date ? format(new Date(p.on_site_date), 'EEE, d MMM yyyy') : null}
              highlight
            />
          </div>

          {(p.trades_required || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {p.trades_required.map((t) => (
                <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
              ))}
            </div>
          )}

          {(p.line_items || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-border overflow-hidden">
              {p.line_items.map((li, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-border last:border-0">
                  <span className="text-muted-foreground">{li.description}</span>
                  <span className="font-mono">{li.quantity} × ${Number(li.unit_price || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}

          {/* AI-estimated breakdown for lump-sum POs (no per-line pricing) */}
          {c.estimated_breakdown && (c.estimated_breakdown.items || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 overflow-hidden" data-testid={`estimate-${c.id}`}>
              <div className="flex items-center gap-2 px-3 py-2 border-b border-primary/20">
                <Calculator className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold">AI rate estimate</span>
                <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">lump sum</Badge>
                <span className="ml-auto text-xs font-bold text-primary" data-testid={`estimate-total-${c.id}`}>
                  ${Number(c.estimated_breakdown.estimated_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {c.estimated_breakdown.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-primary/10 last:border-0">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    {it.description}
                    {it.source === 'unknown' && <Badge variant="outline" className="text-[9px] text-amber-500 border-amber-500/40">no rate</Badge>}
                    {it.margin_pct != null && (
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${it.below_target ? 'text-red-500 border-red-500/40 bg-red-500/10' : 'text-emerald-500 border-emerald-500/40 bg-emerald-500/10'}`} data-testid={`estimate-margin-${c.id}-${i}`}>
                        {it.below_target && '⚠ '}{it.margin_pct}% margin
                      </span>
                    )}
                  </span>
                  <span className="font-mono whitespace-nowrap">
                    {it.estimated_unit_price != null
                      ? `${it.quantity} × $${Number(it.estimated_unit_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                      : '—'}
                  </span>
                </div>
              ))}
              <p className="text-[10px] text-muted-foreground px-3 py-1.5">
                {c.estimated_breakdown.note}
                {c.estimated_breakdown.below_target_count > 0 && (
                  <span className="text-red-500 font-medium"> · {c.estimated_breakdown.below_target_count} line{c.estimated_breakdown.below_target_count > 1 ? 's' : ''} below your {c.estimated_breakdown.target_margin_pct}% margin target</span>
                )}
              </p>
            </div>
          )}

          {/* Rate outlier alerts — this PO prices items above the learned typical */}
          {(c.price_alerts || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2" data-testid={`price-alert-${c.id}`}>
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-500">
                <AlertTriangle className="h-3.5 w-3.5" /> Priced above your typical rates
              </div>
              {c.price_alerts.map((a, i) => (
                <div key={i} className="text-xs text-muted-foreground mt-1">
                  <span className="text-foreground">{a.description}</span>: ${Number(a.price).toLocaleString()} vs typical ${Number(a.typical).toLocaleString()}
                  <span className="text-amber-500 font-medium"> (+{a.pct_above}%)</span>
                </div>
              ))}
            </div>
          )}

          {/* Attachments + links captured from the email */}
          {((c.attachments || []).length > 0 || (c.links || []).length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border">
              {(c.attachments || []).map((a, i) => (
                <span key={`a-${i}`} className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-500/25">
                  <Paperclip className="h-3 w-3" /> {a.filename}
                </span>
              ))}
              {(c.links || []).map((l, i) => (
                <a key={`l-${i}`} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-secondary/70 hover:bg-secondary text-foreground transition-colors">
                  <LinkIcon className="h-3 w-3" /> {l.label}
                </a>
              ))}
              <span className="text-[10px] text-muted-foreground">will be saved to the job on import</span>
            </div>
          )}

          {c.status === 'pending' && (
            <div className="flex flex-wrap items-center justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => handleDismiss(c.id)} data-testid={`dismiss-${c.id}`}>
                <X className="h-4 w-4 mr-1" /> Dismiss
              </Button>
              {c.from_address && (
                <>
                  <Button variant="outline" size="sm" onClick={() => openReply(c, 'accept')} data-testid={`reply-accept-${c.id}`}>
                    <Reply className="h-4 w-4 mr-1" /> Accept & reply
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openReply(c, 'decline')} data-testid={`reply-decline-${c.id}`}>
                    <Reply className="h-4 w-4 mr-1" /> Decline & reply
                  </Button>
                </>
              )}
              <Button size="sm" onClick={() => handleImport(c.id)} data-testid={`import-${c.id}`}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Import as Job
              </Button>
              {(((c.estimated_breakdown?.items || []).some((it) => it.estimated_unit_price != null)) || (c.parsed?.line_items || []).some((li) => li.unit_price)) && (
                <Button variant="outline" size="sm" onClick={() => handleCreateQuote(c.id)} data-testid={`create-quote-${c.id}`}>
                  <FilePlus className="h-4 w-4 mr-1" /> Draft quote
                </Button>
              )}
            </div>
          )}
          {c.replied && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center justify-end gap-1">
              <Reply className="h-3 w-3" /> {c.reply_intent === 'decline' ? 'Declined' : 'Accepted'} reply sent
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="po-inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            PO Inbox
            <span className="text-xs font-semibold px-2 py-1 rounded-full brand-gradient text-white flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> AI
            </span>
          </h1>
          <p className="text-muted-foreground mt-1">AI scans your inbox for new purchase orders and pulls out the details automatically</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={testOpen} onOpenChange={setTestOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="test-email-btn">
                <Wand2 className="h-4 w-4 mr-2" /> Test with Email
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" /> Paste a PO Email — AI will parse it
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From (sender email)</Label>
                    <Input value={testData.from_address} onChange={(e) => setTestData({ ...testData, from_address: e.target.value })} placeholder="po@supplier.com" className="bg-input border-border" />
                  </div>
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input value={testData.subject} onChange={(e) => setTestData({ ...testData, subject: e.target.value })} placeholder="Purchase Order PO-..." className="bg-input border-border" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Email body</Label>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setTestData(sampleEmail)}>
                      Use sample
                    </Button>
                  </div>
                  <Textarea value={testData.body} onChange={(e) => setTestData({ ...testData, body: e.target.value })} rows={10} placeholder="Paste the full email here..." className="bg-input border-border font-mono text-xs" data-testid="test-email-body" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
                <Button onClick={handleTestParse} disabled={testLoading} data-testid="test-parse-btn">
                  {testLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Parsing…</> : <><Sparkles className="h-4 w-4 mr-2" /> Parse with AI</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button onClick={handleScan} disabled={scanning} data-testid="scan-inbox-btn">
            {scanning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning…</> : <><MailSearch className="h-4 w-4 mr-2" /> Scan Inbox</>}
          </Button>
        </div>
      </div>

      {/* Connection status banner */}
      {!account && (
        <div className="surface p-4 flex items-center justify-between gap-4 border-amber-500/30">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <MailSearch className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-sm">No inbox connected</p>
              <p className="text-xs text-muted-foreground">Connect your email in Settings to auto-scan for POs — or try the AI now with “Test with Email”.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => (window.location.href = '/settings')}>Go to Settings</Button>
        </div>
      )}
      {account && (
        <div className="surface p-3 flex items-center gap-3">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
          <p className="text-sm text-muted-foreground">
            Connected to <span className="text-foreground font-medium">{account.email_address}</span>
            {account.last_synced_at ? ` · last scan ${format(new Date(account.last_synced_at), 'd MMM, h:mma')}` : ' · not scanned yet'}
          </p>
        </div>
      )}

      {/* AI Executive Assistant automation panel */}
      {ea && (
        <Card className="surface border-primary/20" data-testid="ea-panel">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center flex-shrink-0 shadow-glow">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold flex items-center gap-2">
                    AI Executive Assistant
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30">
                      always-on
                    </span>
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                    Works while you're on the tools — scans your inbox every {ea.scan_interval_minutes} min, auto-imports
                    confident POs, and emails you a brief the evening before.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={handleTestBrief} disabled={sendingTest || !ea.daily_digest_enabled} data-testid="ea-test-brief-btn">
                  {sendingTest ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><MailCheck className="h-4 w-4 mr-2" /> Send test brief</>}
                </Button>
                <Button variant="outline" onClick={handleWeeklyTest} disabled={sendingWeekly || !ea.weekly_rollup_enabled} data-testid="ea-test-weekly-btn">
                  {sendingWeekly ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><CalendarRange className="h-4 w-4 mr-2" /> Test week-ahead</>}
                </Button>
                <Button onClick={handleRunEa} disabled={runningEa || !account} data-testid="ea-run-now-btn">
                  {runningEa ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Working…</> : <><Zap className="h-4 w-4 mr-2" /> Run assistant now</>}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
              <EaToggle
                testid="ea-autoscan"
                icon={MailSearch}
                title="Auto-scan inbox"
                desc="Check for new POs on a schedule"
                checked={!!ea.auto_scan_enabled}
                onChange={(v) => updateEa({ auto_scan_enabled: v })}
              />
              <EaToggle
                testid="ea-autoimport"
                icon={CheckCircle2}
                title="Auto-import POs"
                desc={`Create jobs at ≥ ${Math.round((ea.auto_import_min_confidence || 0.75) * 100)}% confidence`}
                checked={!!ea.auto_import_enabled}
                onChange={(v) => updateEa({ auto_import_enabled: v })}
              />
              <EaToggle
                testid="ea-digest"
                icon={MailCheck}
                title="Daily brief email"
                desc="Evening summary of tomorrow's jobs"
                checked={!!ea.daily_digest_enabled}
                onChange={(v) => updateEa({ daily_digest_enabled: v })}
              />
            </div>

            <div className="mt-3">
              <EaToggle
                testid="ea-autoack"
                icon={Send}
                title="Auto-acknowledge builders"
                desc="Instantly email the builder to confirm each PO is received & booked in"
                checked={!!ea.auto_acknowledge_enabled}
                onChange={(v) => updateEa({ auto_acknowledge_enabled: v })}
              />
            </div>

            <div className="mt-3">
              <EaToggle
                testid="ea-weekly"
                icon={CalendarRange}
                title="Weekly “week ahead” email (Mondays)"
                desc="Every job booked for the coming week, grouped by day"
                checked={!!ea.weekly_rollup_enabled}
                onChange={(v) => updateEa({ weekly_rollup_enabled: v })}
              />
            </div>

            <div className="mt-3">
              <EaToggle
                testid="ea-thread-tracking"
                icon={Mailbox}
                title="Track email replies & follow-ups"
                desc="Spot date changes, cancellations & variations in reply emails and surface them in your Action List"
                checked={!!ea.thread_tracking_enabled}
                onChange={(v) => updateEa({ thread_tracking_enabled: v })}
              />
            </div>

            <div className="mt-3">
              <EaToggle
                testid="ea-invoice-import"
                icon={FileText}
                title="Auto-import incoming invoices"
                desc="AI reads trade/supplier invoices in your inbox, matches them to the right PO/job and attaches them"
                checked={!!ea.invoice_import_enabled}
                onChange={(v) => updateEa({ invoice_import_enabled: v })}
              />
            </div>

            <div className="mt-3">
              <EaToggle
                testid="ea-payment-reconciliation"
                icon={Calculator}
                title="Reconcile payments from remittances"
                desc="AI reads remittance/payment emails and lines up your invoices to mark paid — you confirm each one"
                checked={!!ea.payment_reconciliation_enabled}
                onChange={(v) => updateEa({ payment_reconciliation_enabled: v })}
              />
            </div>

            {ea.daily_digest_enabled && (
              <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Send brief at</Label>
                  <Select value={String(ea.digest_hour_local ?? 19)} onValueChange={(v) => updateEa({ digest_hour_local: parseInt(v, 10) })}>
                    <SelectTrigger className="bg-input border-border rounded-lg" data-testid="ea-digest-hour"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-64">
                      {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22].map((h) => (
                        <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Timezone</Label>
                  <Select value={ea.timezone || 'Australia/Brisbane'} onValueChange={(v) => updateEa({ timezone: v })}>
                    <SelectTrigger className="bg-input border-border rounded-lg" data-testid="ea-timezone"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {ea.auto_import_enabled && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-muted-foreground">Auto-import confidence threshold</Label>
                  <span className="text-sm font-bold text-primary">{Math.round((ea.auto_import_min_confidence || 0.75) * 100)}%</span>
                </div>
                <Slider
                  min={50}
                  max={95}
                  step={5}
                  value={[Math.round((ea.auto_import_min_confidence || 0.75) * 100)]}
                  onValueChange={(v) => setEa({ ...ea, auto_import_min_confidence: v[0] / 100 })}
                  onValueCommit={(v) => updateEa({ auto_import_min_confidence: v[0] / 100 })}
                  className="w-full"
                  data-testid="ea-confidence-slider"
                />
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  POs below this score stay in “Needs review” so you can approve them manually.
                </p>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-border space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Send reports to</Label>
                <Input
                  key={reports?.recipient_email || 'none'}
                  type="email"
                  defaultValue={reports?.recipient_email || ''}
                  placeholder="Leave blank to use your account email"
                  className="bg-input border-border rounded-lg mt-1.5"
                  data-testid="ea-report-recipient"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (reports?.recipient_email || '')) updateReports({ recipient_email: v });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">CC (comma-separated)</Label>
                <Input
                  key={(reports?.cc_emails || []).join(',') || 'nocc'}
                  type="text"
                  defaultValue={(reports?.cc_emails || []).join(', ')}
                  placeholder="e.g. office@company.com, bookkeeper@company.com"
                  className="bg-input border-border rounded-lg mt-1.5"
                  data-testid="ea-report-cc"
                  onBlur={(e) => {
                    const list = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                    if (list.join(',') !== (reports?.cc_emails || []).join(',')) updateReports({ cc_emails: list });
                  }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Your daily brief &amp; weekly report go to the address above (and any CC). Blank = your login email. Each company sets its own recipients.
              </p>
              {reports?.last_brief_sent_at && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="ea-last-brief-sent">
                  <MailCheck className="h-3.5 w-3.5 text-emerald-400" />
                  Last brief {reports.last_brief_status === 'sent' ? 'sent' : 'attempted'} {formatDistanceToNow(new Date(reports.last_brief_sent_at), { addSuffix: true })}
                  {reports.last_brief_recipient ? ` to ${reports.last_brief_recipient}` : ''}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : candidates.length === 0 ? (
        <div className="surface flex flex-col items-center justify-center py-20 text-muted-foreground">
          <Inbox className="h-14 w-14 mb-4 opacity-50" />
          <p className="font-medium text-foreground">No purchase orders yet</p>
          <p className="text-sm mb-4">Scan your inbox or paste a sample PO email to see the AI in action.</p>
          <Button variant="outline" onClick={() => { setTestData(sampleEmail); setTestOpen(true); }}>
            <Wand2 className="h-4 w-4 mr-2" /> Try with a sample email
          </Button>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <RefreshCw className="h-4 w-4" /> Needs review ({pending.length})
              </h2>
              {pending.map(renderCard)}
            </div>
          )}
          {processed.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Processed ({processed.length})</h2>
              {processed.map(renderCard)}
            </div>
          )}
        </>
      )}

      {/* AI reply dialog */}
      <Dialog open={reply.open} onOpenChange={(o) => !o && setReply((r) => ({ ...r, open: false }))}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Reply className="h-5 w-5 text-primary" />
              {reply.intent === 'decline' ? 'Decline & reply to builder' : 'Accept & reply to builder'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">To</Label>
              <Input value={reply.to} onChange={(e) => setReply((r) => ({ ...r, to: e.target.value }))} className="bg-input border-border rounded-lg" data-testid="reply-to" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Subject</Label>
              <Input value={reply.subject} onChange={(e) => setReply((r) => ({ ...r, subject: e.target.value }))} className="bg-input border-border rounded-lg" data-testid="reply-subject" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Message <span className="text-primary">(AI-drafted — edit as you like)</span></Label>
                <Button variant="ghost" size="sm" onClick={regenReply} disabled={reply.loading} className="h-6 text-xs" data-testid="reply-regen">
                  {reply.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Wand2 className="h-3 w-3 mr-1" /> Redraft</>}
                </Button>
              </div>
              {reply.loading ? (
                <div className="h-36 flex items-center justify-center border border-border rounded-lg bg-input">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : (
                <Textarea value={reply.body} onChange={(e) => setReply((r) => ({ ...r, body: e.target.value }))} rows={7} className="bg-input border-border rounded-lg" data-testid="reply-body" />
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReply((r) => ({ ...r, open: false }))}>Cancel</Button>
              <Button onClick={sendReply} disabled={reply.sending || reply.loading || !reply.to || !reply.body} data-testid="reply-send">
                {reply.sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-2" /> Send reply</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Detail = ({ icon: Icon, label, value, mono, highlight }) => (
  <div className="flex items-center gap-2">
    <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    <span className="text-muted-foreground">{label}:</span>
    <span className={`${mono ? 'font-mono' : ''} ${highlight ? 'text-amber-400 font-medium' : ''} truncate`}>
      {value || <span className="text-muted-foreground/50">—</span>}
    </span>
  </div>
);

const EaToggle = ({ icon: Icon, title, desc, checked, onChange, testid }) => (
  <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-background/40 p-3">
    <div className="flex items-start gap-2.5 min-w-0">
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${checked ? 'text-primary' : 'text-muted-foreground'}`} />
      <div className="min-w-0">
        <p className="text-sm font-medium leading-tight">{title}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export default POInboxPage;
